"use client";

import { useEffect, useState, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { AnimatedCounter, ScrollReveal, DividerRow } from "../shared";
import type { DividerRowItem } from "../shared";
import { ShieldCheckIcon, ActivityIcon, GlobeIcon } from "../shared/icons";
import { walletArt } from "../shared/walletArt";

/**
 * Hero graphic panel geometry (2026-08-04, client).
 *
 * The panel that holds Reqrium3.png keeps ONE composition at every viewport
 * width - anchored to the top-right of its column - and changes only in
 * scale. These three values are what make that true: each interpolates
 * continuously with the viewport instead of stepping at a breakpoint, so
 * there is no width at which the graphic jumps, re-centers, or changes its
 * offset from the card edge.
 *
 * Deliberately NOT Tailwind `sm:`/`lg:` variants. If this needs to change,
 * change the clamp bounds - do not reintroduce breakpoint-conditional
 * padding, radius, margins, or alignment on this panel.
 */
const HERO_PANEL_MAX_WIDTH = "min(100%, 29rem)";
const HERO_PANEL_PAD = "clamp(0.55rem, 2.4vw, 1.25rem)";
const HERO_PANEL_RADIUS = "clamp(0.9rem, 3.4vw, 2.125rem)";

/**
 * Hero grid geometry. The copy column takes whatever is left; the graphic
 * column is a percentage of the row, floored so it never collapses to a
 * sliver on a small phone and capped so it never outgrows the panel on a
 * wide display.
 *
 * This is one continuous expression on purpose - see HERO_PANEL_* above.
 * Replacing it with `grid-cols-1 lg:grid-cols-2` is exactly the bug that
 * was reported twice: below the breakpoint the graphic stops being beside
 * the copy and becomes a stacked row underneath it.
 */
const HERO_GRID_COLS = "minmax(0, 1fr) clamp(5.5rem, 31%, 29rem)";
const HERO_GRID_GAP = "clamp(0.65rem, 2vw, 1.75rem)";

/**
 * Reassurance strip artwork (client-supplied, public/wallet/).
 *
 * Each file already has its message set into the image, so the strip
 * carries no separate text - the wording lives in `alt` instead, which
 * keeps it available to screen readers and crawlers without printing it
 * twice on screen. Intrinsic dimensions are declared so Next.js reserves
 * the right box and the row never shifts as the images decode.
 */
const HERO_PROOF = [
  {
    src: walletArt("noWallet.png"),
    alt: "No wallet connection required to search",
    width: 1536,
    height: 1024,
  },
  {
    src: walletArt("multichain.png"),
    alt: "Multi-chain coverage",
    width: 1024,
    height: 1024,
  },
  {
    src: walletArt("verdict.png"),
    alt: "Sources shown on every verdict",
    width: 1024,
    height: 1024,
  },
] as const;

const HERO_HEADLINES = [
  "for serious blockchain teams.",
  "for live wallet intelligence.",
  "for modern trust operations.",
  "for high-confidence Web3 decisions.",
];

type HeroFeature = {
  title: string;
  detail: string;
  icon: typeof ShieldCheckIcon;
  accentText: string;
  accentRing: string;
  accentSurface: string;
  accentGlow: string;
};

const HERO_FEATURES: HeroFeature[] = [
  {
    title: "Continuous monitoring across chains",
    detail: "Track wallet movement with clearer context across every connected network.",
    icon: ActivityIcon,
    accentText: "text-cyan-100",
    accentRing: "border-cyan-400/18",
    accentSurface: "bg-[linear-gradient(180deg,rgba(9,24,42,0.92),rgba(5,12,24,0.98))]",
    accentGlow: "shadow-[0_18px_42px_-28px_rgba(0,245,255,0.5)]",
  },
  {
    title: "Professional verification workflows",
    detail: "Move from intake to verification with a cleaner, operator-grade trust process.",
    icon: ShieldCheckIcon,
    accentText: "text-sky-50",
    accentRing: "border-sky-400/18",
    accentSurface: "bg-[linear-gradient(180deg,rgba(10,26,50,0.94),rgba(5,14,28,0.98))]",
    accentGlow: "shadow-[0_18px_42px_-28px_rgba(96,165,250,0.45)]",
  },
  {
    title: "Credibility-first onboarding",
    detail: "Present verified signals, trusted entities, and safer decisions from the first touchpoint.",
    icon: GlobeIcon,
    accentText: "text-emerald-50",
    accentRing: "border-emerald-400/18",
    accentSurface: "bg-[linear-gradient(180deg,rgba(8,24,28,0.94),rgba(4,10,22,0.98))]",
    accentGlow: "shadow-[0_18px_42px_-28px_rgba(16,185,129,0.42)]",
  },
];

const HERO_MOBILE_INSIGHTS: HeroFeature[] = [
  {
    title: "Operations",
    detail: "Act on wallet risk and trust signals without leaving the landing flow.",
    icon: ShieldCheckIcon,
    accentText: "text-cyan-100",
    accentRing: "border-cyan-400/16",
    accentSurface: "bg-[linear-gradient(180deg,rgba(9,20,42,0.96),rgba(4,10,24,0.98))]",
    accentGlow: "shadow-[0_18px_40px_-26px_rgba(0,245,255,0.42)]",
  },
  {
    title: "Live Monitoring",
    detail: "See active blockchain movement and shift directly into analysis in one tap.",
    icon: ActivityIcon,
    accentText: "text-emerald-100",
    accentRing: "border-emerald-400/16",
    accentSurface: "bg-[linear-gradient(180deg,rgba(8,24,26,0.96),rgba(4,10,24,0.98))]",
    accentGlow: "shadow-[0_18px_40px_-26px_rgba(16,185,129,0.42)]",
  },
];

const HERO_MOBILE_DECK: HeroFeature[] = [...HERO_FEATURES, ...HERO_MOBILE_INSIGHTS];

type HeroStat = {
  label: string;
  icon: typeof ShieldCheckIcon;
  glow: string;
  render: () => ReactNode;
};

const STATS: HeroStat[] = [
  {
    label: "Wallets Tracked",
    icon: ShieldCheckIcon,
    glow: "#00D4FF",
    render: () => (
      <AnimatedCounter value={1245} suffix="+" className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
    ),
  },
  {
    label: "Transactions",
    icon: ActivityIcon,
    glow: "#A855F7",
    render: () => (
      <AnimatedCounter value={24892} suffix="+" className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
    ),
  },
  {
    label: "Countries",
    icon: GlobeIcon,
    glow: "#5B7FFF",
    render: () => (
      <AnimatedCounter value={120} suffix="+" className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
    ),
  },
];

const STAT_ITEMS: DividerRowItem[] = STATS.map((s) => {
  const Icon = s.icon;
  return {
    key: s.label,
    content: (
      <>
        <Icon size={18} className="shrink-0" style={{ color: s.glow }} />
        <span className="min-w-0">
          {s.render()}
          <span
            className="block text-xs font-medium leading-tight"
            style={{ color: "var(--muted-text, #94a3b8)" }}
          >
            {s.label}
          </span>
        </span>
      </>
    ),
  };
});

/**
 * Reqrium homepage hero: a single bordered glass card containing the brand
 * statement on the left, the eye graphic on the right, and an embedded
 * 3-stat row across the bottom (replacing the old standalone LiveMetricsBar
 * section, which is now unused on the homepage but left on disk).
 *
 * Stat row (Wallets Tracked / Transactions / Countries) and values match
 * the original client-approved "Blockpage411 Homepage" mockup exactly, per
 * explicit request - intentionally superseding the later 2026-06-25 pixel
 * reference's 4-stat variant (Wallets Monitored/Threat Signals/Countries/
 * Coverage), which is no longer used here.
 */
export default function Hero() {
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [featureIndex, setFeatureIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setHeadlineIndex((current) => (current + 1) % HERO_HEADLINES.length);
    }, 3200);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setFeatureIndex((current) => (current + 1) % HERO_MOBILE_DECK.length);
    }, 2800);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <section aria-labelledby="hero-heading" className="page-section relative w-full pt-24 pb-8 sm:pt-32 sm:pb-10">
      <div className="mx-auto max-w-6xl">
        <ScrollReveal>
          <div
            className="section-panel relative overflow-hidden rounded-[32px] border px-5 py-7 sm:px-10 sm:py-12"
            style={{
              // FIX: previous fill (rgba(8,18,42,...) -> rgba(4,10,26,...))
              // was darker than the page background, so the hero "card" had
              // no visible surface of its own - just a border floating over
              // identical background color. Lightened so the panel reads as
              // an elevated glass surface.
              borderColor: "rgba(0,245,255,0.22)",
              background: "linear-gradient(180deg, rgba(26,38,70,0.94), rgba(16,25,46,0.97))",
              boxShadow:
                "0 0 0 1px rgba(0,245,255,0.1) inset, 0 36px 90px -42px rgba(0,0,0,0.9), 0 18px 50px -30px rgba(20,124,255,0.5)",
            }}
          >
            {/* Ambient background glow */}
            <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
              <div
                className="absolute -top-24 left-1/3 h-72 w-72 rounded-full opacity-40 blur-3xl"
                style={{ background: "radial-gradient(circle, rgba(0,212,255,0.25), transparent 70%)" }}
              />
              <div
                className="absolute bottom-0 right-0 h-72 w-72 rounded-full opacity-30 blur-3xl"
                style={{ background: "radial-gradient(circle, rgba(124,58,237,0.25), transparent 70%)" }}
              />
            </div>

            {/* 2026-08-04 (client, round 2): this grid was `grid-cols-1` until
                `lg`, which is the actual reason the graphic "flipped out of
                position" on phones and tablets - below 1024px it wasn't beside
                the copy at all, it was a stacked row underneath it, so the
                rotating headline took the entire top of the screen.
                It is now a two-column grid at EVERY width. The image column is
                sized with a single fluid expression (HERO_GRID_COLS) rather
                than breakpoint variants, so the graphic keeps its top-right
                position next to the dynamic text on a 360px phone exactly as
                it does on desktop - only its width shrinks. */}
            <div
              className="relative z-10 grid"
              style={{
                gridTemplateColumns: HERO_GRID_COLS,
                alignItems: "start",
                columnGap: HERO_GRID_GAP,
                rowGap: HERO_GRID_GAP,
              }}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="eyebrow-label">
                    <ShieldCheckIcon size={14} />
                    Real-Time Protection
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-100">
                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />
                    Multi-chain trust intelligence
                  </span>
                </div>

                <h1
                  id="hero-heading"
                  className="headline-balance mt-5 max-w-2xl font-extrabold leading-[0.95] tracking-[-0.05em]"
                  style={{ fontSize: "clamp(1.55rem, 4.9vw, 4.75rem)" }}
                >
                  Modern trust intelligence
                  {/* Reserved height for the rotating line. The copy column is
                      narrower now that the graphic sits beside it, so the
                      longest headline wraps to more lines on a phone - the
                      floor is raised accordingly so the block below doesn't
                      shift every 3.2s as the text cycles. */}
                  <span className="mt-1 block min-h-[3.3em] xs:min-h-[2.6em] sm:min-h-[1.8em]">
                    <span
                      key={HERO_HEADLINES[headlineIndex]}
                      className="block animate-[fadeHeadline_3200ms_ease-in-out] bg-[linear-gradient(90deg,#eaf0ff_0%,#9ae6ff_28%,#4fb2ff_58%,#d8b4fe_100%)] bg-clip-text text-transparent"
                      aria-live="polite"
                    >
                      {HERO_HEADLINES[headlineIndex]}
                    </span>
                  </span>
                </h1>
                <p
                  className="mt-4 max-w-xl font-medium leading-relaxed"
                  style={{ color: "rgba(222, 234, 255, 0.88)", fontSize: "clamp(0.9rem, 1.55vw, 1.48rem)" }}
                >
                  Reqrium gives operators one decisive surface for wallet analysis, entity verification, live transaction monitoring, and reputation defense.
                </p>
                <span
                  className="bg-brand-gradient-divider mt-5 block h-1 w-20 rounded-full"
                  aria-hidden="true"
                />

                <p
                  className="prose-muted mt-6 max-w-2xl leading-relaxed"
                  style={{ color: "rgba(168, 184, 210, 0.84)", fontSize: "clamp(0.8rem, 1.05vw, 1.05rem)" }}
                >
                  Trace wallets, verify entities, analyze risk, and protect users across multiple blockchains in one secure, operator-grade platform built for credibility, speed, and confidence.
                </p>

                {/* Premium pass: both CTAs now use the shared .pr-btn
                    primitives (globals.css) so they match the navbar's
                    primary button exactly - same radius, motion curve,
                    sheen sweep, and focus ring - instead of being a
                    one-off gradient defined here. */}
                <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  {/* Primary CTA (client, 2026-08-04): the flat brand gradient
                      is replaced by public/wallet/analysis.png. The button
                      keeps its exact previous footprint - same .pr-btn
                      geometry, height, radius, motion and sheen - the artwork
                      simply becomes its surface. A scrim sits between the
                      image and the label so the text keeps its contrast
                      against the artwork's bright areas (the illustration has
                      white coins and light paper in it, which plain white
                      text would disappear into). */}
                  <Link href="/wallet-analysis" className="pr-btn pr-btn-image pr-btn-fluid">
                    <Image
                      src={walletArt("analysis.png")}
                      alt=""
                      fill
                      sizes="(max-width: 640px) 22rem, 16rem"
                      className="pr-btn-image-art"
                      aria-hidden="true"
                    />
                    <span className="pr-btn-image-scrim" aria-hidden="true" />
                    <span>Start Wallet Analysis</span>
                  </Link>
                  <Link href="/transactions/live" className="pr-btn pr-btn-ghost pr-btn-fluid">
                    <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-70" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300" />
                    </span>
                    <span>Watch Live Activity</span>
                  </Link>
                </div>

              </div>

              {/* Reassurance strip (client, 2026-08-04): the three
                  checkmark-and-text proof points are now the supplied
                  artwork, which already carries its own wording - so the
                  duplicate text labels were removed rather than repeated
                  under each image. The wording survives as `alt` text, so
                  screen readers and search engines still get it.

                  This is a full-width row of the hero grid, not a child of
                  the copy column: at phone widths the copy column is narrow,
                  and three images crammed into it would be unreadable. Being
                  an in-flow grid row it also cannot overlap the CTAs above
                  it or the feature deck below it at any width - each image
                  sits inside its own fixed-aspect box with `object-contain`,
                  so nothing bleeds outside its card either. */}
              <ul
                className="m-0 grid list-none grid-cols-1 p-0 xs:grid-cols-3"
                style={{ gridColumn: "1 / -1", gap: "clamp(0.6rem, 1.6vw, 1rem)" }}
              >
                {HERO_PROOF.map((proof, index) => (
                  <li
                    key={proof.src}
                    className="pr-proof"
                    // Staggered so the three cards' light rings aren't in
                    // lockstep - they read as three live surfaces rather
                    // than one animation applied three times.
                    style={{ animationDelay: `${index * -3.3}s` }}
                  >
                    <span className="pr-proof-inner">
                      <span className="pr-proof-well">
                        <Image
                          src={proof.src}
                          alt={proof.alt}
                          width={proof.width}
                          height={proof.height}
                          sizes="(max-width: 480px) 94vw, (max-width: 1024px) 31vw, 22rem"
                          className="pr-proof-art"
                        />
                      </span>
                    </span>
                  </li>
                ))}
              </ul>

              {/* The rotating insight card and the feature decks below are
                  full-width rows of the hero grid (gridColumn 1/-1), not
                  children of the copy column. With the graphic now pinned
                  beside the copy at every width, the copy column is narrow on
                  a phone - these cards need the whole panel width to stay
                  readable. The graphic itself is explicitly placed at
                  row 1 / column 2 further down, so it stays top-right
                  regardless of source order. */}
              <div className="block sm:hidden" style={{ gridColumn: "1 / -1" }}>
                  {(() => {
                    const feature = HERO_MOBILE_DECK[featureIndex % HERO_MOBILE_DECK.length];
                    const Icon = feature.icon;
                    return (
                      <div
                        key={feature.title}
                        className={`relative overflow-hidden rounded-[28px] border border-transparent px-4 py-4 transition-all duration-700 ${feature.accentSurface} ${feature.accentGlow}`}
                      >
                        <div
                          className="pointer-events-none absolute inset-0 rounded-[28px]"
                          style={{
                            boxShadow: 'inset 0 0 0 1px rgba(78, 96, 128, 0.28)',
                            background: 'radial-gradient(circle at top right, rgba(255,255,255,0.08), transparent 34%)',
                          }}
                          aria-hidden="true"
                        />
                        <div className="relative flex items-start gap-3">
                          <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border bg-black/20 ${feature.accentRing} ${feature.accentText}`}>
                            <Icon size={18} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${feature.accentText}`}>
                              Live feature focus
                            </div>
                            <div className="mt-2 text-base font-semibold leading-snug text-slate-100">
                              {feature.title}
                            </div>
                            <div className="mt-2 text-[13px] leading-relaxed text-slate-300">
                              {feature.detail}
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-3">
                              <span className={`inline-flex items-center gap-2 rounded-full border border-white/8 bg-black/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${feature.accentText}`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                                Rotating insight
                              </span>
                              <div className="flex items-center gap-1.5">
                                {HERO_MOBILE_DECK.map((item, index) => (
                                  <span
                                    key={item.title}
                                    className={`h-1.5 rounded-full transition-all ${
                                      index === featureIndex % HERO_MOBILE_DECK.length ? 'w-5 bg-slate-100' : 'w-1.5 bg-slate-500/60'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="hidden grid-cols-1 gap-3 sm:grid sm:grid-cols-3" style={{ gridColumn: "1 / -1" }}>
                  {HERO_FEATURES.map((feature, index) => {
                    const Icon = feature.icon;
                    const active = featureIndex % HERO_FEATURES.length === index;
                    return (
                      <div
                        key={feature.title}
                        className={`relative overflow-hidden rounded-[24px] px-4 py-4 transition-all duration-500 ${feature.accentSurface} ${feature.accentGlow} ${
                          active
                            ? `${feature.accentRing} translate-y-0 border`
                            : 'border border-slate-800/70 opacity-88'
                        }`}
                      >
                        <div
                          className={`pointer-events-none absolute inset-x-0 top-0 h-16 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),transparent)] transition-opacity duration-500 ${
                            active ? 'opacity-100' : 'opacity-40'
                          }`}
                          aria-hidden="true"
                        />
                        <div className="relative flex items-start gap-3">
                          <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border bg-black/15 ${feature.accentRing} ${feature.accentText}`}>
                            <Icon size={18} />
                          </span>
                          <div className="min-w-0">
                            <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${active ? feature.accentText : 'text-slate-400'}`}>
                              {active ? 'Feature spotlight' : 'Feature'}
                            </div>
                            <div className={`mt-2 text-sm font-semibold leading-snug ${active ? 'text-slate-100' : 'text-slate-200'}`}>
                              {feature.title}
                            </div>
                            <div className={`mt-2 text-[13px] leading-relaxed ${active ? 'text-slate-300' : 'text-slate-400'}`}>
                              {feature.detail}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="hidden grid-cols-1 gap-3 xs:grid-cols-2 sm:grid lg:hidden" style={{ gridColumn: "1 / -1" }}>
                  {HERO_MOBILE_INSIGHTS.map((feature, index) => {
                    const Icon = feature.icon;
                    const active = featureIndex % HERO_MOBILE_INSIGHTS.length === index;
                    return (
                      <div
                        key={feature.title}
                        className={`group relative overflow-hidden rounded-[28px] p-4 transition-all duration-500 ${feature.accentSurface} ${feature.accentGlow} ${
                          active ? `${feature.accentRing} border` : 'border border-slate-800/75'
                        }`}
                      >
                        <div
                          className={`pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_38%)] transition-opacity duration-500 ${
                            active ? 'opacity-100' : 'opacity-40'
                          }`}
                          aria-hidden="true"
                        />
                        <div className="relative flex items-start gap-3">
                          <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border bg-black/20 ${feature.accentRing} ${feature.accentText}`}>
                            <Icon size={18} />
                          </span>
                          <div>
                            <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${active ? feature.accentText : 'text-slate-400'}`}>
                              {feature.title}
                            </div>
                            <div className={`mt-2 text-sm font-medium leading-relaxed ${active ? 'text-slate-200' : 'text-slate-300'}`}>
                              {feature.detail}
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                              <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-cyan-300' : 'bg-slate-500'}`} />
                              <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${active ? 'text-slate-200' : 'text-slate-500'}`}>
                                {active ? 'Active focus' : 'Queued insight'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

              {/* Trust badge + hero image.
                  2026-08-04 (client): this block used to change its anchoring
                  across breakpoints - centered on mobile, then top/right-aligned
                  with negative margin offsets from `lg` up - plus different
                  padding, corner radius, and badge offsets at each tier. The
                  result was that the graphic visibly jumped as the viewport
                  crossed 1024px, and sat in a different place relative to the
                  card on phones than it does on desktop.
                  It is now anchored top-right at EVERY breakpoint and only its
                  scale changes: max-width, padding, and radius interpolate
                  continuously with clamp() instead of flipping at a
                  breakpoint, so the composition is identical on a phone and on
                  a 27" display - just smaller. Nothing here is
                  breakpoint-conditional any more, by design; please keep it
                  that way.

                  Explicitly placed at row 1 / column 2. Explicit placement is
                  resolved before auto-placement, so the graphic holds the
                  top-right cell no matter where it sits in source order - the
                  full-width feature rows above simply flow into rows 2+. */}
              <div
                className="relative flex items-start justify-end lg:min-h-[29rem]"
                style={{ gridColumn: "2", gridRow: "1" }}
              >
                <div
                  className="pointer-events-none absolute right-0 top-0 h-56 rounded-full bg-cyan-400/10 blur-3xl"
                  style={{ width: "min(24rem, 100%)" }}
                  aria-hidden="true"
                />
                {/* The clamp() values are written as inline styles rather than
                    Tailwind arbitrary utilities on purpose: they're the load-
                    bearing part of "same composition, different scale", and
                    inline styles can't be dropped by a content-scan miss or a
                    future Tailwind config change. HERO_PANEL_* below are the
                    single source of truth - the badge insets reuse the same
                    expression as the panel padding, so the badge is always
                    exactly one padding-unit from the corner. */}
                <div
                  className="relative ml-auto w-full bg-white/[0.055] shadow-[0_30px_80px_-48px_rgba(0,245,255,0.45)]"
                  style={{
                    maxWidth: HERO_PANEL_MAX_WIDTH,
                    padding: HERO_PANEL_PAD,
                    borderRadius: HERO_PANEL_RADIUS,
                  }}
                >
                  {/* Auxiliary chrome only. Hidden below `md` because at that
                      size the graphic column is ~100px wide and the badge
                      would overrun it - the IMAGE itself never moves, which
                      is the part that was reported. */}
                  <div
                    className="pointer-events-none absolute hidden rounded-full border border-white/10 bg-slate-950/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-200 md:block"
                    style={{ right: HERO_PANEL_PAD, top: HERO_PANEL_PAD }}
                  >
                    Trust command
                  </div>
                  <div
                    className="absolute inset-x-0 top-0 h-24 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),transparent)]"
                    style={{ borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit" }}
                    aria-hidden="true"
                  />
                  {/* Full width, no max-w cap: the image always fills exactly the space the card's
                      own padding leaves for it, at every breakpoint - it can't leave leftover gap,
                      overflow, or drift, because its size is 100% derived from the card around it.
                      Soft gradient backing + drop shadow only - no border/ring outline. */}
                  <div className="flex" style={{ paddingTop: "clamp(1rem, 2.2vw, 1.4rem)" }}>
                    <div
                      className="w-full overflow-hidden bg-gradient-to-b from-white/[0.06] to-black/30 shadow-[0_16px_40px_-16px_rgba(0,0,0,0.6)]"
                      style={{ borderRadius: "clamp(0.9rem, 2vw, 1.15rem)" }}
                    >
                      <Image
                        src="/sponsors/Reqrium3.png"
                        alt="Reqrium real-time trust intelligence eye graphic"
                        width={1254}
                        height={1254}
                        priority
                        className="h-auto w-full object-contain"
                      />
                    </div>
                  </div>
                  {/* In normal document flow (not absolutely positioned) so it always sits cleanly
                      below the image instead of floating over it and risking overlap at sizes
                      where the (now full-width) image runs taller. */}
                  <div
                    className="hidden border border-cyan-400/18 bg-slate-950/65 backdrop-blur-xl md:block"
                    style={{
                      marginTop: "clamp(0.75rem, 1.8vw, 1rem)",
                      borderRadius: "clamp(1rem, 2.4vw, 1.375rem)",
                      padding: "clamp(0.7rem, 1.8vw, 0.85rem)",
                    }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-100">Signal cockpit</div>
                        <div className="mt-1 text-sm font-semibold text-slate-100">One surface for analysis, verification, and live movement.</div>
                      </div>
                      <div className="rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-100">
                        Live
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Embedded stats row (replaces the old standalone LiveMetricsBar) */}
            <DividerRow
              className="relative z-10 mt-10"
              background="rgba(8,12,22,0.5)"
              cellClassName="flex flex-1 items-center min-w-0 gap-2.5 px-4 py-3.5"
              items={STAT_ITEMS}
            />
          </div>
        </ScrollReveal>
      </div>
      <style jsx>{`
        @keyframes fadeHeadline {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          12%, 78% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-10px);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-\[fadeHeadline_3200ms_ease-in-out\] {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
}
